import { z } from 'zod';
import type { WorklistFilterParams } from '@/Models/GenericWorklist/WorklistParams';
import type Status from '@/Models/Shared/Status';
import { STATUS } from '@/Models/Shared/Status';

// 3 months ~= 3 * 30.4 (average days/month) ~= 91.3 days, rounded up to 92 so a full calendar quarter is
// never rejected purely because of rounding.
export const MAX_DATE_RANGE_DAYS = 92;
export const MIN_PATIENT_NAME_LENGTH = 4;

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const isNonEmpty = (value: string | undefined | null): boolean => (value ?? '').trim().length > 0;

const isBoundedDateRange = (preset: string | undefined, from: Date | null | undefined, until: Date | null | undefined): boolean => {
    if (isNonEmpty(preset)) {
        return true; // All built-in presets (today, yesterday, 2d, 7d, 30d) are well within the limit.
    }

    if (!from) {
        return false;
    }

    if (!until) {
        return false;
    }

    const spanInDays = Math.abs(until.getTime() - from.getTime()) / MILLISECONDS_PER_DAY;
    return spanInDays <= MAX_DATE_RANGE_DAYS;
};

const statusListExcludesFinished = (statuses: Status[] | undefined): boolean =>
    !!statuses && statuses.length > 0 && !statuses.includes(STATUS.Finished);

const isDateRangeCriterionSatisfied = (filter: WorklistFilterParams | undefined): boolean =>
    isBoundedDateRange(filter?.DateRangePreset, filter?.From, filter?.Until)
    || isBoundedDateRange(filter?.ReportCreatedDateRangePreset, filter?.ReportCreatedFrom, filter?.ReportCreatedUntil)
    || isBoundedDateRange(filter?.ReportModifiedDateRangePreset, filter?.ReportModifiedFrom, filter?.ReportModifiedUntil);

const isStatusExcludingFinishedCriterionSatisfied = (filter: WorklistFilterParams | undefined): boolean =>
    statusListExcludesFinished(filter?.ProcedureStatuses) || statusListExcludesFinished(filter?.OrderStatuses);

const isPatientIdentifierCriterionSatisfied = (filter: WorklistFilterParams | undefined): boolean =>
    isNonEmpty(filter?.ExternalID) || isNonEmpty(filter?.PatientNationalNumber);

const isPatientNameCriterionSatisfied = (filter: WorklistFilterParams | undefined): boolean =>
    (filter?.PatientFirstName ?? '').trim().length >= MIN_PATIENT_NAME_LENGTH
    || (filter?.PatientLastName ?? '').trim().length >= MIN_PATIENT_NAME_LENGTH;

// i18n key describing why a filter isn't specific enough.
export const WORKLIST_MINIMUM_CRITERIA_MESSAGE_KEY = 'Worklist.Filter.MinimumCriteriaNotMet';

// `z.union` succeeds as soon as any one member schema parses successfully
const asWorklistFilterCriterion = (predicate: (filter: WorklistFilterParams | undefined) => boolean) =>
    z.custom<WorklistFilterParams | undefined>((value) => predicate(value as WorklistFilterParams | undefined));

const worklistFilterCriteriaSchema = z.union(
    [
        asWorklistFilterCriterion(isDateRangeCriterionSatisfied),
        asWorklistFilterCriterion(isStatusExcludingFinishedCriterionSatisfied),
        asWorklistFilterCriterion(isPatientIdentifierCriterionSatisfied),
        asWorklistFilterCriterion(isPatientNameCriterionSatisfied),
    ],
    { error: () => WORKLIST_MINIMUM_CRITERIA_MESSAGE_KEY },
);

export const validateWorklistFilterCriteria = (filter: WorklistFilterParams | undefined): z.ZodSafeParseResult<WorklistFilterParams | undefined> =>
    worklistFilterCriteriaSchema.safeParse(filter);

export const worklistHasMinimumCriteria = (filter: WorklistFilterParams | undefined): boolean =>
    validateWorklistFilterCriteria(filter).success;

export const getWorklistFilterCriteriaMessageKey = (filter: WorklistFilterParams | undefined): string | undefined => {
    const result = validateWorklistFilterCriteria(filter);
    return result.success ? undefined : result.error.issues[0]?.message;
};
